import { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp } from 'lucide-react';

const RISK_COLORS = {
  low: '#10b981', medium: '#f59e0b', high: '#ef4444', critical: '#dc2626',
};

const WHAT_LIVE = [
  'Multi-agent analysis pipeline (3 AI agents)',
  'Ambiguity scoring with explainable breakdown',
  'Risk flags with operational consequences',
  'Precision rewrite + acceptance criteria generation',
  'Kanban workflow with auto-routing',
  'One-click export to clipboard (formatted markdown)',
  'Open API endpoint — any system can POST text',
  'Stateless processing — no customer data stored',
  'Works across healthcare, government, enterprise, legal',
];

const ROADMAP = [
  'API authentication, rate limiting, and developer documentation',
  'Jira & Azure DevOps plugin (analyze tickets in-place)',
  'Cross-ticket conflict detection (Agent 4)',
  'Domain compliance packs (healthcare, fintech, government)',
  'Team dashboards with historical risk trends',
  'Edge case generator agent (Agent 5)',
];

function getRiskColor(level) {
  return RISK_COLORS[level] || '#71717a';
}

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div style={{ background: '#141419', border: '1px solid #1f1f2e', borderRadius: 8, padding: '8px 12px' }}>
        <div style={{ color: payload[0].payload.color, fontWeight: 700, fontSize: 13, textTransform: 'capitalize' }}>{payload[0].name}</div>
        <div style={{ color: '#a1a1aa', fontSize: 12 }}>{payload[0].value} tickets</div>
      </div>
    );
  }
  return null;
};

function CountUp({ target, suffix = '', color }) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const num = typeof target === 'string' ? parseInt(target) : target;
    if (!num || isNaN(num)) { setValue(target); return; }
    let current = 0;
    const step = num / 32;
    const timer = setInterval(() => {
      current = Math.min(current + step, num);
      setValue(Math.round(current));
      if (current >= num) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [target]);

  return <span style={{ color }}>{value}{suffix}</span>;
}

export default function ImpactTab({ tickets, analysisLog = [] }) {
  const analyzed = tickets.filter(t => t.analysis?.assessment);
  const scores = analyzed.map(t => t.analysis.assessment.ambiguityScore || 0);
  const avgScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  const totalPhrases = analyzed.reduce((sum, t) => sum + (t.analysis?.detection?.flaggedPhrases?.length || 0), 0);
  const totalRiskFlags = analyzed.reduce((sum, t) => sum + (t.analysis?.assessment?.riskFlags?.length || 0), 0);
  const totalCriteria = analyzed.reduce((sum, t) => sum + (t.analysis?.remediation?.acceptanceCriteria?.length || 0), 0);

  const actionedTickets = tickets.filter(t => t.column === 'refined' || t.column === 'ready_for_dev');
  const hoursSaved = actionedTickets.reduce((sum, t) => sum + Math.round((t.analysis?.assessment?.ambiguityScore || 0) * 0.5), 0);

  const refinedWithHistory = tickets.filter(t =>
    t.column === 'refined' && t.originalScore != null && t.analysis?.assessment?.ambiguityScore != null && t.originalScore !== t.analysis.assessment.ambiguityScore
  );
  let heroLabel = '';
  let heroValue = null;
  if (refinedWithHistory.length > 0) {
    const avgOriginal = refinedWithHistory.reduce((s, t) => s + t.originalScore, 0) / refinedWithHistory.length;
    const avgCurrent = refinedWithHistory.reduce((s, t) => s + t.analysis.assessment.ambiguityScore, 0) / refinedWithHistory.length;
    heroValue = Math.round(((avgOriginal - avgCurrent) / avgOriginal) * 100);
    heroLabel = `avg ambiguity reduction across ${refinedWithHistory.length} refined ticket${refinedWithHistory.length > 1 ? 's' : ''}`;
  } else if (analyzed.length > 0) {
    heroValue = Math.round(100 - avgScore * 0.35);
    heroLabel = 'estimated reduction if all tickets rewritten';
  }

  const riskDist = ['low', 'medium', 'high', 'critical'].map(level => ({
    name: level, value: analyzed.filter(t => t.analysis.assessment.riskLevel === level).length, color: RISK_COLORS[level],
  })).filter(d => d.value > 0);

  const scoreColor = avgScore > 60 ? '#ef4444' : avgScore > 35 ? '#f59e0b' : '#10b981';

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }} className="scrollbar-hide">
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'Tickets Scanned', raw: analyzed.length, icon: '📊', color: '#6366f1', suffix: '' },
            { label: 'Avg. Ambiguity Score', raw: avgScore || 0, icon: '🎯', color: scoreColor, suffix: '' },
            { label: 'Phrases Flagged', raw: totalPhrases, icon: '🔍', color: '#f59e0b', suffix: '' },
            { label: 'Hours Saved', raw: hoursSaved, icon: '⏱️', color: '#10b981', suffix: 'h', arrow: true },
          ].map((stat, i) => (
            <div key={i} data-testid={`stat-card-${i}`} className="stat-card"
              style={{ background: '#141419', border: '1px solid #1f1f2e', borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ fontSize: 22, marginBottom: 10 }}>{stat.icon}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1 }}>
                  {stat.raw > 0 ? <CountUp target={stat.raw} suffix={stat.suffix} color={stat.color} /> : <span style={{ color: stat.color }}>{stat.raw === 0 ? '0' : '—'}{stat.suffix}</span>}
                </div>
                {stat.arrow && hoursSaved > 0 && <TrendingUp size={14} color="#10b981" />}
              </div>
              <div style={{ color: '#52525b', fontSize: 11, fontWeight: 600, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {heroValue !== null && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(16,185,129,0.08) 100%)',
            border: '1px solid rgba(99,102,241,0.2)', borderRadius: 12,
            padding: '24px 28px', marginBottom: 24, textAlign: 'center',
          }}>
            {refinedWithHistory.length > 0 ? (
              <>
                <div style={{ color: '#10b981', fontSize: 48, fontWeight: 800, letterSpacing: '-0.04em' }}>
                  <CountUp target={heroValue} suffix="%" color="#10b981" />
                </div>
                <div style={{ color: '#a1a1aa', fontSize: 15, marginTop: 4 }}>{heroLabel}</div>
              </>
            ) : (
              <>
                <div style={{ color: '#10b981', fontSize: 48, fontWeight: 800, letterSpacing: '-0.04em' }}>
                  {heroValue}%
                </div>
                <div style={{ color: '#a1a1aa', fontSize: 15, marginTop: 4 }}>{heroLabel}</div>
                <div style={{ color: '#3f3f46', fontSize: 12, marginTop: 6 }}>
                  Accept rewrites and re-analyze to see real reduction
                </div>
              </>
            )}
          </div>
        )}

        {analyzed.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px', color: '#3f3f46', fontSize: 14, marginBottom: 24 }}>
            Analyze tickets to see impact metrics
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
          <div style={{ background: '#141419', border: '1px solid #1f1f2e', borderRadius: 12, padding: '20px' }}>
            <div style={{ color: '#f4f4f5', fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Risk Distribution</div>
            {riskDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={riskDist} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
                    {riskDist.map((entry, i) => <Cell key={i} fill={entry.color} stroke="transparent" />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" iconSize={8}
                    formatter={(v) => <span style={{ color: '#71717a', fontSize: 12, textTransform: 'capitalize' }}>{v}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3f3f46', fontSize: 13 }}>
                Analyze tickets to see distribution
              </div>
            )}
          </div>

          <div style={{ background: '#141419', border: '1px solid #1f1f2e', borderRadius: 12, padding: '20px' }}>
            <div style={{ color: '#f4f4f5', fontSize: 14, fontWeight: 700, marginBottom: 20 }}>Agent Performance</div>
            {[
              { icon: '🔍', label: 'Detection Agent', value: `${totalPhrases} phrases flagged`, color: '#6366f1' },
              { icon: '📊', label: 'Assessment Agent', value: `${totalRiskFlags} risk flags identified`, color: '#f59e0b' },
              { icon: '✍️', label: 'Rewrite Agent', value: `${totalCriteria} acceptance criteria`, color: '#10b981' },
            ].map((agent, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: i < 2 ? '1px solid #1f1f2e' : 'none' }}>
                <span style={{ fontSize: 22 }}>{agent.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#a1a1aa', fontSize: 12, fontWeight: 600 }}>{agent.label}</div>
                  <div style={{ color: agent.color, fontSize: 13, fontWeight: 700, marginTop: 2 }}>{agent.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: '#141419', border: '1px solid #1f1f2e', borderRadius: 12, padding: '20px', marginBottom: 24 }}>
          <div style={{ color: '#f4f4f5', fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Analysis Log</div>
          {analysisLog.length === 0 ? (
            <div style={{ color: '#3f3f46', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
              No activity yet. Analyze a ticket to see the log.
            </div>
          ) : (
            <div style={{ maxHeight: 220, overflowY: 'auto' }} className="scrollbar-hide">
              {analysisLog.map((entry, i) => (
                <div key={entry.id} style={{
                  display: 'flex', gap: 12, padding: '8px 0',
                  borderBottom: i < analysisLog.length - 1 ? '1px solid #1a1a24' : 'none',
                  alignItems: 'flex-start',
                }}>
                  <span style={{ color: '#3f3f46', fontSize: 11, flexShrink: 0, fontFamily: 'monospace', marginTop: 1 }}>{entry.time}</span>
                  <span style={{ color: '#71717a', fontSize: 12, lineHeight: 1.5 }}>
                    {entry.type === 'analyzed' ? (
                      <><strong style={{ color: '#a1a1aa' }}>{entry.title}</strong> analyzed → Score: <strong style={{ color: getRiskColor(entry.riskLevel) }}>{entry.score}</strong> ({entry.riskLevel} risk)</>
                    ) : (
                      <><strong style={{ color: '#10b981' }}>✓</strong> Rewrite accepted for <strong style={{ color: '#a1a1aa' }}>{entry.title}</strong></>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 24, background: '#141419', border: '1px solid #1f1f2e', borderRadius: 10, padding: '16px 20px', textAlign: 'center' }}>
          <div style={{ color: '#a1a1aa', fontSize: 14, lineHeight: 1.6 }}>
            Developers spend <strong style={{ color: '#f4f4f5' }}>17.3 hours/week</strong> dealing with bad requirements — that's <strong style={{ color: '#f4f4f5' }}>$85B in annual rework</strong> across the industry.
          </div>
          <div style={{ color: '#3f3f46', fontSize: 11, marginTop: 6 }}>Source: Stripe Developer Coefficient Report</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ background: '#141419', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 12, padding: '20px' }}>
            <div style={{ color: '#10b981', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>✅ What's Live Today</div>
            {WHAT_LIVE.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 7 }}>
                <span style={{ color: '#10b981', fontSize: 12, flexShrink: 0 }}>✅</span>
                <span style={{ color: '#71717a', fontSize: 12, lineHeight: 1.4 }}>{item}</span>
              </div>
            ))}
          </div>
          <div style={{ background: '#141419', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 12, padding: '20px' }}>
            <div style={{ color: '#818cf8', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>🔜 Roadmap</div>
            {ROADMAP.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 7 }}>
                <span style={{ color: '#6366f1', fontSize: 12, flexShrink: 0 }}>🔜</span>
                <span style={{ color: '#71717a', fontSize: 12, lineHeight: 1.4 }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
